import React, { useState } from 'react';
import { Eye, Pencil, Trash, Send, FileText } from 'lucide-react';
import './QuoteTrackerTable.css';
import '../Invoice/Toast.css';
import { useToast } from '../Invoice/Toast';
import supabase from '../../supabase';

const QuoteTrackerTable = ({
  filteredQuotes,
  clients,
  updatingStatus,
  sendingQuotation,
  loading,
  onQuoteClick,
  onEditQuote,
  onStatusUpdate,
  onSendQuotationEmail,
  onDeleteQuote,
  onCreateQuotationClick,
  onQuotesRefresh
}) => {
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();
  const [generatingInvoice, setGeneratingInvoice] = useState({});
  const [confirmingInvoice, setConfirmingInvoice] = useState(null);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  // Open delete popup
  const openDeletePopup = (quote) => {
    setQuoteToDelete(quote);
    setShowDeletePopup(true);
  };

  // Close delete popup
  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setQuoteToDelete(null);
  };

  // Handle delete confirmation
  const confirmDelete = async () => {
    if (!quoteToDelete) return;
    
    const quote = quoteToDelete;
    closeDeletePopup();
    
    try {
      await onDeleteQuote(quote);
      showSuccess('Quote Deleted', 'Quotation deleted successfully.');
    } catch (error) {
      showError('Delete Failed', 'Failed to delete quotation. Please try again.');
    }
  };

  // Handler for generating invoice from quote
  const handleGenerateInvoice = async (quote) => {
    if (quote.invoice_created) {
      showError('Invoice Exists', 'Invoice already generated for this quote.');
      return;
    }

    if (!quote.email_sent) {
      showError('Cannot Generate Invoice', 'Quote must be sent to client before generating invoice.');
      return;
    }

    if (!quote.status) {
      showError('Cannot Generate Invoice', 'Quote must be accepted before generating invoice.');
      return;
    }

    setConfirmingInvoice(quote);
  };

  // Confirm and proceed with invoice generation
  const confirmGenerateInvoice = async () => {
    const quote = confirmingInvoice;
    setConfirmingInvoice(null);

    if (!quote) return;

    try {
      setGeneratingInvoice(prev => ({ ...prev, [quote.id]: true }));

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('generate-quote-invoice', {
        body: { 
          quoteId: quote.id,
          userId: user.id 
        }
      });

      if (error) {
        if (error.context) {
          const errorData = await error.context.json();
          throw new Error(errorData.error || 'Failed to generate invoice');
        } else {
          throw new Error(error.message || 'Network error: Unable to reach server');
        }
      }

      quote.invoice_created = true;
      
      const clientName = clients[quote.client_id] || 'Client';
      
      showSuccess(
        'Invoice Generated Successfully!',
        `Invoice ${data.invoice.invoice_number} created for ${clientName}. Amount: ₹${parseFloat(data.invoice.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
      );
      
      if (onQuotesRefresh) {
        onQuotesRefresh();
      }

    } catch (err) {
      console.error('Error generating invoice:', err);
      showError('Invoice Generation Failed', err.message);
    } finally {
      setGeneratingInvoice(prev => ({ ...prev, [quote.id]: false }));
    }
  };

  const handleStatusUpdate = async (quoteId, newStatus) => {
    const quote = filteredQuotes.find(q => q.id === quoteId);
    
    if (!quote?.email_sent) {
      showError('Status Update Failed', 'Cannot change status. Quote must be sent first.');
      return;
    }

    try {
      await onStatusUpdate(quoteId, newStatus);
      const quoteNumber = quote.quotation_number || 'Quote';
      showSuccess(
        'Status Updated', 
        `Quote ${quoteNumber} marked as ${newStatus ? 'accepted' : 'not accepted'}.`
      );
    } catch (error) {
      showError('Update Failed', 'Error updating quote status. Please try again.');
    }
  };

  const handleSendQuotation = async (quote) => {
    const clientName = clients[quote.client_id] || 'Client';
    
    try {
      await onSendQuotationEmail(quote);
      showSuccess('Quotation Sent', `Quote sent successfully to ${clientName}!`);
    } catch (error) {
      showError('Send Failed', `Failed to send quotation: ${error.message}`);
    }
  };

  const handleEdit = (quote) => {
    if (quote.email_sent) {
      showWarning(
        'Edit Warning', 
        'This quote has already been sent. Editing may cause confusion with the client.'
      );
    }
    onEditQuote(quote);
  };

  const handleView = (quote) => {
    onQuoteClick(quote);
  };

  const StatusToggle = ({ status, onChange, disabled, emailSent }) => (
    <button
      type="button"
      className={`QuoteTrackertable-status-toggle ${status ? 'accepted' : 'not-accepted'} ${disabled || !emailSent ? 'disabled' : ''}`}
      onClick={() => emailSent && !disabled ? onChange(!status) : null}
      disabled={disabled || !emailSent}
      title={!emailSent ? 'Quote must be sent before status can be changed' : ''}
    >
      <div className="toggle-knob"></div>
      <span className="status-text">
        {!emailSent ? 'Draft' : (status ? 'Accepted' : 'Not Accepted')}
      </span>
    </button>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (filteredQuotes.length === 0) {
    return (
      <>
        <div className="QuoteTrackertable-empty-state">
          <div className="QuoteTrackertable-empty-icon">📋</div>
          <h3 className="QuoteTrackertable-empty-title">
            No quotations found
          </h3>
          <p className="QuoteTrackertable-empty-description">
            Try adjusting your search or filter criteria, or create your first quotation.
          </p>
          <button 
            className="QuoteTrackertable-create-first-quote-btn" 
            onClick={onCreateQuotationClick}
          >
            Create Your First Quotation
          </button>
        </div>
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      {/* Generate Invoice Confirmation Popup */}
      {confirmingInvoice && (
        <div className="quote-delete-popup-overlay" onClick={() => setConfirmingInvoice(null)}>
          <div className="quote-delete-popup" onClick={(e) => e.stopPropagation()}>
            <div className="quote-delete-popup-header">
              <div className="quote-delete-popup-icon-wrapper">
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              <h3 className="quote-delete-popup-title">Generate Invoice</h3>
            </div>
            
            <div className="quote-delete-popup-content">
              <p className="quote-delete-popup-message">
                Are you sure you want to generate an invoice for quote <strong>{confirmingInvoice.quotation_number || 'Unnamed'}</strong>
                {clients[confirmingInvoice.client_id] && ` for ${clients[confirmingInvoice.client_id]}`}?
              </p>
              
              <div className="quote-delete-popup-info">
                <div className="quote-delete-popup-info-item">
                  <span className="quote-delete-popup-label">Quote Amount:</span>
                  <span className="quote-delete-popup-value">
                    ₹{parseFloat(confirmingInvoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="quote-delete-popup-info-item">
                  <span className="quote-delete-popup-label">Invoice Number:</span>
                  <span className="quote-delete-popup-value">
                    Q-{confirmingInvoice.quotation_number}
                  </span>
                </div>
              </div>

              <div className="quote-delete-popup-warning">
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <span>This will create a new invoice based on this quotation.</span>
              </div>
            </div>
            
            <div className="quote-delete-popup-actions">
              <button 
                className="quote-delete-popup-btn quote-delete-popup-btn-cancel"
                onClick={() => setConfirmingInvoice(null)}
              >
                Cancel
              </button>
              
              <button 
                className="quote-delete-popup-btn quote-delete-popup-btn-confirm"
                onClick={confirmGenerateInvoice}
              >
                Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Quote Confirmation Popup */}
      {showDeletePopup && quoteToDelete && (
        <div className="quote-delete-popup-overlay" onClick={closeDeletePopup}>
          <div className="quote-delete-popup" onClick={(e) => e.stopPropagation()}>
            <div className="quote-delete-popup-header">
              <div className="quote-delete-popup-icon-wrapper">
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <h3 className="quote-delete-popup-title">Delete Quotation</h3>
            </div>
            
            <div className="quote-delete-popup-content">
              <p className="quote-delete-popup-message">
                Are you sure you want to delete quote <strong>{quoteToDelete.quotation_number || 'Unnamed'}</strong>
                {clients[quoteToDelete.client_id] && ` for ${clients[quoteToDelete.client_id]}`}?
              </p>
              
              <div className="quote-delete-popup-info">
                <div className="quote-delete-popup-info-item">
                  <span className="quote-delete-popup-label">Amount:</span>
                  <span className="quote-delete-popup-value">
                    ₹{parseFloat(quoteToDelete.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {quoteToDelete.status !== null && (
                  <div className="quote-delete-popup-info-item">
                    <span className="quote-delete-popup-label">Status:</span>
                    <span className={`quote-delete-popup-status ${quoteToDelete.status ? 'accepted' : 'not-accepted'}`}>
                      {quoteToDelete.status ? 'Accepted' : 'Not Accepted'}
                    </span>
                  </div>
                )}
              </div>

              <div className="quote-delete-popup-warning">
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>This action cannot be undone.</span>
              </div>
            </div>
            
            <div className="quote-delete-popup-actions">
              <button 
                className="quote-delete-popup-btn quote-delete-popup-btn-cancel"
                onClick={closeDeletePopup}
              >
                Cancel
              </button>
              
              <button 
                className="quote-delete-popup-btn quote-delete-popup-btn-delete"
                onClick={confirmDelete}
              >
                Delete Quote
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="QuoteTrackertable-container">
        {/* Desktop Table View */}
        <div className="QuoteTrackertable-table-view">
          <div className="QuoteTrackertable-wrapper">
            <table className="QuoteTrackertable-table">
              <thead className="QuoteTrackertable-header">
                <tr>
                  <th>Quote #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Issue Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="QuoteTrackertable-body">
                {filteredQuotes.map(quote => (
                  <tr key={quote.id} className="QuoteTrackertable-row">
                    <td className="QuoteTrackertable-cell">
                      <span className="QuoteTrackertable-cell-content">
                        {quote.quotation_number || 'Unnamed'}
                      </span>
                    </td>
                    <td className="QuoteTrackertable-cell">
                      <span className="QuoteTrackertable-cell-content">
                        {clients[quote.client_id] || 'Unknown Client'}
                      </span>
                    </td>
                    <td className="QuoteTrackertable-cell">
                      <span className="QuoteTrackertable-cell-content QuoteTrackertable-amount">
                        ₹{parseFloat(quote.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="QuoteTrackertable-cell">
                      <span className="QuoteTrackertable-cell-content">
                        {formatDate(quote.created_at)}
                      </span>
                    </td>
                    <td className="QuoteTrackertable-cell">
                      <div className="QuoteTrackertable-status-container">
                        <StatusToggle
                          status={quote.status === true}
                          onChange={(newStatus) => handleStatusUpdate(quote.id, newStatus)}
                          disabled={updatingStatus[quote.id]}
                          emailSent={quote.email_sent === true}
                        />
                      </div>
                    </td>
                    <td className="QuoteTrackertable-cell">
                      <div className="QuoteTrackertable-actions-container">
                        <button
                          className="QuoteTrackertable-action-btn QuoteTrackertable-view-btn"
                          onClick={() => handleView(quote)}
                          disabled={loading}
                          title="View Quote"
                        >
                          <Eye size={16} className="QuoteTrackertable-icon" />
                        </button>
                        
                        {quote.email_sent && quote.status && !quote.invoice_created && (
                          <button
                            className="QuoteTrackertable-action-btn QuoteTrackertable-invoice-btn"
                            onClick={() => handleGenerateInvoice(quote)}
                            disabled={generatingInvoice[quote.id]}
                            title="Generate Invoice"
                          >
                            {generatingInvoice[quote.id] ? (
                              <div className="QuoteTrackertable-spinner"></div>
                            ) : (
                              <FileText size={16} className="QuoteTrackertable-icon" />
                            )}
                          </button>
                        )}
                        
                        {!quote.email_sent && (
                          <button
                            className="QuoteTrackertable-action-btn QuoteTrackertable-edit-btn"
                            onClick={() => handleEdit(quote)}
                            title="Edit Quote"
                          >
                            <Pencil size={16} className="QuoteTrackertable-icon" />
                          </button>
                        )}
                        
                        {!quote.email_sent && (
                          <button
                            className="QuoteTrackertable-action-btn QuoteTrackertable-send-btn"
                            onClick={() => handleSendQuotation(quote)}
                            disabled={sendingQuotation[quote.id]}
                            title="Send Quote"
                          >
                            {sendingQuotation[quote.id] ? (
                              <div className="QuoteTrackertable-spinner"></div>
                            ) : (
                              <Send size={16} className="QuoteTrackertable-icon" />
                            )}
                          </button>
                        )}
                        
                        <button
                          className="QuoteTrackertable-action-btn QuoteTrackertable-delete-btn"
                          onClick={() => openDeletePopup(quote)}
                          title="Delete Quote"
                        >
                          <Trash size={16} className="QuoteTrackertable-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="QuoteTrackertable-card-view">
          {filteredQuotes.map(quote => (
            <div key={quote.id} className="QuoteTrackertable-card">
              <div className="QuoteTrackertable-card-header">
                <div className="QuoteTrackertable-card-title">
                  <h4>{quote.quotation_number || 'Unnamed'}</h4>
                  <span className="QuoteTrackertable-card-amount">
                    ₹{parseFloat(quote.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="QuoteTrackertable-card-status">
                  <StatusToggle
                    status={quote.status === true}
                    onChange={(newStatus) => handleStatusUpdate(quote.id, newStatus)}
                    disabled={updatingStatus[quote.id]}
                    emailSent={quote.email_sent === true}
                  />
                </div>
              </div>
              
              <div className="QuoteTrackertable-card-body">
                <div className="QuoteTrackertable-card-field">
                  <span className="QuoteTrackertable-card-label">Client:</span>
                  <span className="QuoteTrackertable-card-value">{clients[quote.client_id] || 'Unknown Client'}</span>
                </div>
                <div className="QuoteTrackertable-card-field">
                  <span className="QuoteTrackertable-card-label">Issue Date:</span>
                  <span className="QuoteTrackertable-card-value">{formatDate(quote.created_at)}</span>
                </div>
              </div>
              
              <div className="QuoteTrackertable-card-actions">
                <button
                  className="QuoteTrackertable-action-btn QuoteTrackertable-view-btn"
                  onClick={() => handleView(quote)}
                  disabled={loading}
                  title="View Quote"
                >
                  <Eye size={16} className="QuoteTrackertable-icon" />
                </button>
                
                {quote.email_sent && quote.status && !quote.invoice_created && (
                  <button
                    className="QuoteTrackertable-action-btn QuoteTrackertable-invoice-btn"
                    onClick={() => handleGenerateInvoice(quote)}
                    disabled={generatingInvoice[quote.id]}
                    title="Generate Invoice"
                  >
                    {generatingInvoice[quote.id] ? (
                      <div className="QuoteTrackertable-spinner"></div>
                    ) : (
                      <FileText size={16} className="QuoteTrackertable-icon" />
                    )}
                  </button>
                )}
                
                {!quote.email_sent && (
                  <button
                    className="QuoteTrackertable-action-btn QuoteTrackertable-edit-btn"
                    onClick={() => handleEdit(quote)}
                    title="Edit Quote"
                  >
                    <Pencil size={16} className="QuoteTrackertable-icon" />
                  </button>
                )}
                
                {!quote.email_sent && (
                  <button
                    className="QuoteTrackertable-action-btn QuoteTrackertable-send-btn"
                    onClick={() => handleSendQuotation(quote)}
                    disabled={sendingQuotation[quote.id]}
                    title="Send Quote"
                  >
                    {sendingQuotation[quote.id] ? (
                      <div className="QuoteTrackertable-spinner"></div>
                    ) : (
                      <Send size={16} className="QuoteTrackertable-icon" />
                    )}
                  </button>
                )}
                
                <button
                  className="QuoteTrackertable-action-btn QuoteTrackertable-delete-btn"
                  onClick={() => openDeletePopup(quote)}
                  title="Delete Quote"
                >
                  <Trash size={16} className="QuoteTrackertable-icon" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default QuoteTrackerTable;